import {
  ShippingAutoOrderFromPartnerStateEnum,
  ShippingDeliveryBasisEnum,
  ShippingDeliveryTypeEnum,
  ShippingPositionNamingEnum,
  ShippingStateEnum,
  WarehouseInstructionEnum,
} from '__generated__/types';
import { capitalizeFirstLetter } from 'utils/capitalizeFirstLetter';

export enum ShippingDirection {
  C2P = 'c2p',
  C2X = 'c2x',
  P2C = 'p2c',
  P2P = 'p2p',
  P2X = 'p2x',
  X2C = 'x2c',
  X2P = 'x2p',
}

export enum ShippingSource {
  Customer = 'customer',
  Provider = 'provider',
  Xometry = 'xometry',
}

export enum ShippingProcessingStatus {
  // ...2X
  NotProcessed = 'not_processed',
  InProcessing = 'in_processing',
  PartiallyProcessed = 'partially_processed',
  ProcessedForRepack = 'processed_for_repack',
  ProcessedForReturn = 'processed_for_return',
  ProcessedForStorage = 'processed_for_storage',
  ProcessedAndSplit = 'processed_and_split',

  // ...2C
  Accepted = 'accepted',
  Claim = 'claim',

  // ...2P
  ReceivedSuccessfully = 'received_successfully',
}

export enum ShippingState {
  PmDraft = 'pm_draft',
  Packing = 'packing',
  Draft = 'draft',
  BoxReady = 'box_ready',
  ShippingOrdered = 'shipping_ordered',
  ReadyForShipping = 'ready_for_shipping',
  InTransit = 'in_transit',
  Delivered = 'delivered',
  Canceled = 'canceled',
}

export const autoOrderLockedStates = [
  ShippingAutoOrderFromPartnerStateEnum.NotChecked,
  ShippingAutoOrderFromPartnerStateEnum.WaitsForHsCodes,
  ShippingAutoOrderFromPartnerStateEnum.Ordered,
];

export const shippingPositionNamingLabels: Record<ShippingPositionNamingEnum, string> = {
  [ShippingPositionNamingEnum.Customs]: 'Customs',
  [ShippingPositionNamingEnum.Clients]: 'Clients',
  [ShippingPositionNamingEnum.CustomsAndClients]: 'Customs & Clients',
};

export const deliveryBasis = Object.values(ShippingDeliveryBasisEnum).map((value) => ({
  value,
  label: value,
}));

export const deliveryTypes = Object.values(ShippingDeliveryTypeEnum).map((value) => ({
  value,
  label: capitalizeFirstLetter(value),
}));

export const processingStatuses = [
  {
    value: ShippingProcessingStatus.NotProcessed,
    label: 'Not yet processed',
    directions: [
      ShippingDirection.C2P,
      ShippingDirection.C2X,
      ShippingDirection.P2C,
      ShippingDirection.P2P,
      ShippingDirection.P2X,
      ShippingDirection.X2C,
      ShippingDirection.X2P,
    ],
  },
  {
    value: ShippingProcessingStatus.InProcessing,
    label: 'In processing (on QC)',
    directions: [ShippingDirection.C2X, ShippingDirection.P2X],
  },
  {
    value: ShippingProcessingStatus.PartiallyProcessed,
    label: 'Partially processed (manual)',
    directions: [ShippingDirection.C2X, ShippingDirection.P2X],
  },
  {
    value: ShippingProcessingStatus.ProcessedForRepack,
    label: 'Processed for repack (X2C)',
    directions: [ShippingDirection.C2X, ShippingDirection.P2X],
  },
  {
    value: ShippingProcessingStatus.ProcessedForReturn,
    label: 'Processed for return (X2P)',
    directions: [ShippingDirection.C2X, ShippingDirection.P2X],
  },
  {
    value: ShippingProcessingStatus.ProcessedForStorage,
    label: 'Processed for Inventory / Util',
    directions: [ShippingDirection.C2X, ShippingDirection.P2X],
  },
  {
    value: ShippingProcessingStatus.ProcessedAndSplit,
    label: 'Processed & split',
    directions: [ShippingDirection.C2X, ShippingDirection.P2X],
  },
  {
    value: ShippingProcessingStatus.Accepted,
    label: 'Accepted',
    directions: [ShippingDirection.P2C, ShippingDirection.X2C],
  },
  {
    value: ShippingProcessingStatus.Claim,
    label: 'Claim',
    directions: [ShippingDirection.P2C, ShippingDirection.X2C],
  },
  {
    value: ShippingProcessingStatus.ReceivedSuccessfully,
    label: 'Received successfully',
    directions: [ShippingDirection.C2P, ShippingDirection.P2P, ShippingDirection.X2P],
  },
];

export const nonDirectShippingDirections = [
  {
    value: ShippingDirection.P2X,
    label: 'Partner to Xometry',
  },
  {
    value: ShippingDirection.X2P,
    label: 'Xometry to Partner (repair)',
  },
  {
    value: ShippingDirection.C2X,
    label: 'Customer to Xometry (return)',
  },
  {
    value: ShippingDirection.X2C,
    label: 'Xometry to Customer',
  },
];

export const shippingDirections = [
  {
    value: ShippingDirection.C2P,
    label: 'Customer to Partner (direct return)',
  },
  {
    value: ShippingDirection.P2P,
    label: 'Partner to Partner',
  },
  {
    value: ShippingDirection.P2C,
    label: 'Partner to Customer (direct shipping)',
  },
  ...nonDirectShippingDirections,
];

export interface IShippingStatusLabeled {
  value: ShippingState;
  label: string;
}

export const shippingStatuses: Array<IShippingStatusLabeled> = [
  {
    value: ShippingState.PmDraft,
    label: 'PM Draft',
  },
  {
    value: ShippingState.Packing,
    label: 'Packing',
  },
  {
    value: ShippingState.Draft,
    label: 'Draft',
  },
  {
    value: ShippingState.BoxReady,
    label: 'Box Ready',
  },
  {
    value: ShippingState.ShippingOrdered,
    label: 'Shipping Ordered',
  },
  {
    value: ShippingState.ReadyForShipping,
    label: 'Ready For Shipping',
  },
  {
    value: ShippingState.InTransit,
    label: 'In Transit',
  },
  {
    value: ShippingState.Delivered,
    label: 'Delivered',
  },
  {
    value: ShippingState.Canceled,
    label: 'Canceled',
  },
];

export const shippingStates: Array<{ value: ShippingStateEnum; label: string }> = [
  {
    value: ShippingStateEnum.PmDraft,
    label: 'PM Draft',
  },
  {
    value: ShippingStateEnum.Packing,
    label: 'Packing',
  },
  {
    value: ShippingStateEnum.Draft,
    label: 'Draft',
  },
  {
    value: ShippingStateEnum.BoxReady,
    label: 'Box Ready',
  },
  {
    value: ShippingStateEnum.ShippingOrdered,
    label: 'Shipping Ordered',
  },
  {
    value: ShippingStateEnum.ReadyForShipping,
    label: 'Ready For Shipping',
  },
  {
    value: ShippingStateEnum.InTransit,
    label: 'In Transit',
  },
  {
    value: ShippingStateEnum.Delivered,
    label: 'Delivered',
  },
  {
    value: ShippingStateEnum.Canceled,
    label: 'Canceled',
  },
];

export const shippingProcessingInstructions = [
  {
    value: WarehouseInstructionEnum.Repack,
    label: 'Repack for Customer',
  },
  {
    value: WarehouseInstructionEnum.Xqc,
    label: 'Xometry QC',
  },
  {
    value: WarehouseInstructionEnum.Store,
    label: 'Xometry Store',
  },
  {
    value: WarehouseInstructionEnum.Scrap,
    label: 'Xometry Scrap',
  },
  {
    value: WarehouseInstructionEnum.Repack2p,
    label: 'Repack for Provider',
  },
];

export enum ShippingHsCode {
  Confirmed = 'confirmed',
  Standard = 'standard',
}

export const shippingHsCodeLabels: Record<ShippingHsCode, string> = {
  [ShippingHsCode.Confirmed]: '🟢 Confirmed',
  [ShippingHsCode.Standard]: '🟡 History / Client input / Empty',
};

export const shippingHSCodeStateOptions = Object.values(ShippingHsCode).map((state) => ({
  value: state,
  label: shippingHsCodeLabels[state],
}));

export const customerObjectAllowedDirections = [
  ShippingDirection.P2C,
  ShippingDirection.P2X,
  ShippingDirection.P2P,
  ShippingDirection.C2P,
  ShippingDirection.C2X,
];

export const INTERCOMPANY_ROLE = {
  Prod: 'prod',
  Sales: 'sales',
  Source: 'source',
  Target: 'target',
  Rfq: 'rfq',
} as const;
